import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { bomCapturaApi } from '@/services/api'
import { ImagenLightbox, type LightboxInfo } from './ImagenLightbox'

interface Props {
  itemId: string
  empresa: string
  formula: string
  productnumber?: string
  capturaId?: string
  dark: boolean
  className?: string
  alt?: string
  /** Si true, al hacer clic abre el visor a pantalla completa. */
  zoomable?: boolean
  /** Información adicional a mostrar en el visor (junto a la imagen). */
  info?: LightboxInfo
  /** Nodo a mostrar cuando no hay imagen (en vez del "—"). Conserva el className
   *  del recuadro para no aplastarse. */
  placeholder?: ReactNode
  /** Si true y no hay imagen, no renderiza nada (ni recuadro gris). Para modo vista. */
  hideWhenEmpty?: boolean
}

/** Carga perezosa de una imagen base64 (data-URI) desde Dataverse, con visor opcional. */
export function BomImagen({ itemId, empresa, formula, productnumber, capturaId, dark, className, alt = '', zoomable, info, placeholder, hideWhenEmpty }: Props) {
  const [open, setOpen] = useState(false)
  const enabled = Boolean(itemId && empresa && formula && (productnumber || capturaId))
  const { data, isLoading } = useQuery({
    queryKey: capturaId
      ? ['bom-img-cap', itemId, empresa, formula, capturaId]
      : ['bom-img', itemId, empresa, formula, productnumber],
    queryFn: () =>
      capturaId
        ? bomCapturaApi.getCapturaImagen(itemId, empresa, formula, capturaId)
        : bomCapturaApi.getImagen(itemId, empresa, formula, productnumber as string),
    enabled,
    staleTime: 5 * 60 * 1000,
  })

  const ph = `flex items-center justify-center text-[11px] ${dark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'}`

  if (isLoading) return <div className={`${className ?? ''} ${ph} animate-pulse`}>…</div>
  if (!data) return hideWhenEmpty ? null : <div className={`${className ?? ''} ${ph}`}>{placeholder ?? '—'}</div>

  return (
    <>
      <img
        src={data}
        alt={alt}
        className={`${className ?? ''} ${zoomable ? 'cursor-zoom-in' : ''}`}
        onClick={zoomable ? () => setOpen(true) : undefined}
      />
      {open && <ImagenLightbox src={data} alt={alt} onClose={() => setOpen(false)} info={info} />}
    </>
  )
}
